import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { TruncatedCell } from '../components/common/TruncatedCell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Search, Edit, Trash2, Eye, MoreVertical, TicketCheck, MessageSquare, Loader2, IndianRupee, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { FileUpload } from '../components/FileUpload';
import { toLocalISODate, formatDate } from '../lib/formatUtils';
import { Autocomplete } from '../components/Autocomplete';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { Checkbox } from '../components/ui/checkbox';
import { exportToExcel } from '../lib/exportUtils';
import { usePageSize } from '../context/PageSizeContext';
import { Pagination } from '../components/Pagination';

const priorityOptions = ['critical', 'high', 'medium', 'low'];
const statusOptions = ['open', 'in_progress', 'resolved', 'closed'];
const categoryOptions = ['bug', 'feature', 'support', 'question'];

export const TicketsPage = () => {
  const confirm = useConfirmation();
  const { pageSize } = usePageSize();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('tickets.create');
  const canEdit = hasPermission('tickets.edit');
  const canDelete = hasPermission('tickets.delete');
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ subject: '', client_name: '', assigned_to: '', priority: 'medium', status: 'open', category: 'support', description: '', is_chargeable: false, charge_amount: '' });
  const [attachments, setAttachments] = useState([]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [searchQuery, statusFilter]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get(`/tickets?${params.toString()}`);
      if (res.data.success) {
        setTickets(res.data.data);
        setTotal(res.data.total || 0);
      }
    } catch {
      setTickets([]);
      setTotal(0);
    } finally { setLoading(false); }
  }, [page, pageSize, searchQuery, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/tickets/stats/summary');
      if (res.data.success) setStats(res.data.data);
    } catch { /* keep prev */ }
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchTickets, 250);
    return () => clearTimeout(t);
  }, [fetchTickets]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      charge_amount: formData.is_chargeable ? parseFloat(formData.charge_amount) || 0 : 0,
    };
    try {
      if (isEditing && selectedTicket) {
        await api.put(`/tickets/${selectedTicket.id}`, payload);
        toast.success('Ticket updated');
      } else {
        payload.ticket_id = `TKT-${String(Date.now()).slice(-6)}`;
        payload.created_at = toLocalISODate();
        await api.post('/tickets', payload);
        toast.success('Ticket created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTickets();
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (t) => {
    const confirmed = await confirm({ title: 'Delete Ticket', description: `Are you sure you want to delete ticket ${t.ticket_id}? This action cannot be undone.` });
    if (!confirmed) return;
    try {
      await api.delete(`/tickets/${t.id}`);
      toast.success('Ticket deleted');
      fetchTickets();
      fetchStats();
    } catch { toast.error('Failed to delete'); }
  };

  const handleEdit = (t) => {
    setSelectedTicket(t);
    setFormData({ subject: t.subject, client_name: t.client_name, assigned_to: t.assigned_to, priority: t.priority, status: t.status, category: t.category, description: t.description || '', is_chargeable: t.is_chargeable || false, charge_amount: String(t.charge_amount || '') });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const resetForm = () => { setFormData({ subject: '', client_name: '', assigned_to: '', priority: 'medium', status: 'open', category: 'support', description: '', is_chargeable: false, charge_amount: '' }); setSelectedTicket(null); setIsEditing(false); setAttachments([]); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ limit: '10000' });
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get(`/tickets?${params.toString()}`);
      const rows = res.data?.data || [];
      if (!rows.length) { toast.info('No tickets to export'); return; }
      exportToExcel(rows.map(t => ({
        ticket_id: t.ticket_id,
        subject: t.subject,
        client_name: t.client_name,
        assigned_to: t.assigned_to,
        priority: t.priority,
        status: t.status,
        category: t.category,
        is_chargeable: t.is_chargeable ? 'Yes' : 'No',
        charge_amount: t.charge_amount || 0,
        created_at: t.created_at,
      })), [
        { key: 'ticket_id', header: 'Ticket' },
        { key: 'subject', header: 'Subject' },
        { key: 'client_name', header: 'Client' },
        { key: 'assigned_to', header: 'Assigned' },
        { key: 'priority', header: 'Priority' },
        { key: 'status', header: 'Status' },
        { key: 'category', header: 'Category' },
        { key: 'is_chargeable', header: 'Chargeable' },
        { key: 'charge_amount', header: 'Amount (INR)' },
        { key: 'created_at', header: 'Created' },
      ], 'Tickets');
      toast.success(`Exported ${rows.length} tickets`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
    finally { setExporting(false); }
  };

  const getStatusBadge = (status) => {
    const cfg = { open: 'bg-blue-500/10 text-blue-500', in_progress: 'bg-amber-500/10 text-amber-500', resolved: 'bg-emerald-500/10 text-emerald-500', closed: 'bg-gray-500/10 text-gray-400' };
    return <Badge className={cfg[status] || cfg.open}>{status.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (p) => {
    const cfg = { critical: 'bg-red-500/10 text-red-500', high: 'bg-orange-500/10 text-orange-500', medium: 'bg-amber-500/10 text-amber-500', low: 'bg-gray-500/10 text-gray-400' };
    return <Badge className={cfg[p] || cfg.medium}>{p}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tickets-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Support Tickets</h1><p className="text-muted-foreground">Manage client support requests</p></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="export-tickets-excel">
            {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
            Excel
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
            {canCreate && (<DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-ticket-btn"><Plus className="h-4 w-4 mr-2" />New Ticket</Button></DialogTrigger>)}
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{isEditing ? 'Edit Ticket' : 'Create Ticket'}</DialogTitle><DialogDescription>Enter ticket details</DialogDescription></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2"><Label>Subject *</Label><Input value={formData.subject} onChange={e => setFormData({ ...formData, subject: e.target.value })} required data-testid="ticket-subject" /></div>
                  <div className="space-y-2 col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      rows={4}
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe the issue in detail — steps to reproduce, screenshots links, expected vs actual…"
                      data-testid="ticket-description"
                    />
                  </div>
                  <div className="space-y-2"><Label>Client *</Label>
                    <Autocomplete
                      value={formData.client_name}
                      onChange={v => setFormData({ ...formData, client_name: v })}
                      fetchOptions={async (q) => {
                        const res = await api.get(`/clients?search=${q}&limit=10`);
                        return res.data.success ? res.data.data.map(c => c.name || c.company_name) : [];
                      }}
                      placeholder="Search clients..."
                      data-testid="ticket-client"
                    />
                  </div>
                  <div className="space-y-2"><Label>Assigned To</Label>
                    <Autocomplete
                      value={formData.assigned_to}
                      onChange={v => setFormData({ ...formData, assigned_to: v })}
                      fetchOptions={async (q) => {
                        const res = await api.get(`/employees?search=${q}&status=active&limit=10`);
                        return res.data.success ? res.data.data.map(e => `${e.first_name} ${e.last_name}`) : [];
                      }}
                      fetchDefaults={async () => {
                        const res = await api.get('/smb/users/recently-assigned?limit=5');
                        return res.data.success ? res.data.data.map(u => `${u.first_name} ${u.last_name}`) : [];
                      }}
                      placeholder="Search employees..."
                      data-testid="ticket-assigned"
                    />
                  </div>
                  <div className="space-y-2"><Label>Priority</Label><Select value={formData.priority} onValueChange={v => setFormData({ ...formData, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{priorityOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-2"><Label>Category</Label><Select value={formData.category} onValueChange={v => setFormData({ ...formData, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="space-y-2"><Label>Attachments</Label><FileUpload files={attachments} onChange={setAttachments} maxFiles={5} /></div>
                {/* Chargeable ticket */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border">
                  <Checkbox checked={formData.is_chargeable} onCheckedChange={v => setFormData({ ...formData, is_chargeable: !!v })} id="chargeable" data-testid="ticket-chargeable" />
                  <Label htmlFor="chargeable" className="text-sm cursor-pointer flex-1">Chargeable ticket</Label>
                  {formData.is_chargeable && (
                    <Input type="number" value={formData.charge_amount} onChange={e => setFormData({ ...formData, charge_amount: e.target.value })} placeholder="Amount (INR)" className="w-36 h-8" data-testid="ticket-charge-amount" />
                  )}
                </div>
                <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="ticket-submit-btn">{isEditing ? 'Update' : 'Create'}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card><CardContent className="py-4"><div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search tickets..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="ticket-search" /></div>
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-full sm:w-[180px]" data-testid="ticket-status-filter"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{statusOptions.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent></Select>
      </div></CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><TicketCheck className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold" data-testid="ticket-stat-total">{stats.total}</p><p className="text-xs text-muted-foreground">Total</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><TicketCheck className="h-5 w-5 text-blue-500" /></div><div><p className="text-2xl font-bold" data-testid="ticket-stat-open">{stats.open}</p><p className="text-xs text-muted-foreground">Open</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><MessageSquare className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold" data-testid="ticket-stat-progress">{stats.in_progress}</p><p className="text-xs text-muted-foreground">In Progress</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><TicketCheck className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold" data-testid="ticket-stat-resolved">{stats.resolved}</p><p className="text-xs text-muted-foreground">Resolved</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <TicketCheck className="h-12 w-12 mb-4 opacity-50" />
            <p>No tickets match the current filters</p>
          </div>
        ) : (
        <Table data-testid="tickets-table"><TableHeader><TableRow><TableHead>Ticket</TableHead><TableHead>Subject</TableHead><TableHead>Client</TableHead><TableHead>Assigned</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>Charge</TableHead><TableHead>Date</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{tickets.map(t => (
          <TableRow key={t.id} data-testid={`ticket-row-${t.id}`}>
            <TableCell className="font-mono text-sm">{t.ticket_id}</TableCell>
            <TableCell className="font-medium">
              <TruncatedCell text={t.subject} maxWidth={220} testId={`ticket-subject-${t.id}`} />
            </TableCell>
            <TableCell>{t.client_name}</TableCell>
            <TableCell>{t.assigned_to}</TableCell>
            <TableCell>{getPriorityBadge(t.priority)}</TableCell>
            <TableCell>{getStatusBadge(t.status)}</TableCell>
            <TableCell>
              {t.is_chargeable ? <Badge className="bg-amber-500/10 text-amber-500"><IndianRupee className="h-3 w-3 mr-0.5" />{(t.charge_amount || 0).toLocaleString('en-IN')}</Badge> : <span className="text-muted-foreground text-sm">-</span>}
            </TableCell>
            <TableCell className="text-sm">{formatDate(t.created_at)}</TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { setSelectedTicket(t); setIsViewOpen(true); }}><Eye className="h-4 w-4 mr-2" />View</DropdownMenuItem>
                {canEdit && (<DropdownMenuItem onClick={() => handleEdit(t)} data-testid={`ticket-edit-${t.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                {canDelete && (<DropdownMenuItem onClick={() => handleDelete(t)} className="text-destructive focus:text-destructive" data-testid={`ticket-delete-${t.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>))}</TableBody></Table>)}
      </CardContent></Card>

      <Pagination
        page={page}
        total={total}
        pageSize={pageSize}
        onPageChange={setPage}
        testIdPrefix="tickets-pagination"
      />

      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>Ticket Details</DialogTitle></DialogHeader>
        {selectedTicket && <div className="space-y-4">
          <div className="flex justify-between"><div><p className="font-mono text-sm text-muted-foreground">{selectedTicket.ticket_id}</p><p className="font-bold text-lg">{selectedTicket.subject}</p></div>{getStatusBadge(selectedTicket.status)}</div>
          {selectedTicket.description && (
            <div className="border-t border-border pt-3">
              <p className="text-xs text-muted-foreground mb-1">Description</p>
              <p className="text-sm whitespace-pre-wrap break-words" data-testid="ticket-description-view">
                {selectedTicket.description}
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 text-sm border-t border-border pt-3">
            <div><p className="text-muted-foreground">Client</p><p className="font-medium">{selectedTicket.client_name}</p></div>
            <div><p className="text-muted-foreground">Assigned To</p><p className="font-medium">{selectedTicket.assigned_to}</p></div>
            <div><p className="text-muted-foreground">Priority</p>{getPriorityBadge(selectedTicket.priority)}</div>
            <div><p className="text-muted-foreground">Category</p><Badge variant="outline">{selectedTicket.category}</Badge></div>
            {selectedTicket.is_chargeable && <div className="col-span-2"><p className="text-muted-foreground">Charge Amount</p><p className="font-semibold text-amber-500"><IndianRupee className="inline h-4 w-4" />{(selectedTicket.charge_amount || 0).toLocaleString('en-IN')}</p></div>}
          </div>
        </div>}
      </DialogContent></Dialog>
    </div>
  );
};
